/* src/components/customGame/setup/panels/stimuli/SymbolicStimulusPanel.js */
import React from 'react';
import PropTypes from 'prop-types';

// Re-using the SubSettingsSection helper
const SubSettingsSection = ({ title, children, helpText = "" }) => (
    <div className="mb-4 pt-3">
        <label className="block text-sm font-semibold text-gray-700 mb-1">{title}</label>
        {helpText && <p className="text-xs text-gray-500 mb-2">{helpText}</p>}
        <div className="space-y-3">
            {children}
        </div>
    </div>
);

SubSettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
};

// Define some predefined sets for convenience
const PREDEFINED_SYMBOL_SETS = {
    "Phonologically Distinct Letters (Research)": "B,K,Q,T,H,M,N,P,X,R", // From research doc
    "Standard English Consonants": "B,C,D,F,G,H,J,K,L,M,N,P,Q,R,S,T,V,W,X,Y,Z",
    "Digits (0-9)": "0,1,2,3,4,5,6,7,8,9",
    "Playing Card Suits (Names)": "Club,Diamond,Heart,Spade", // Use names, actual symbols for rendering
    // "Greek Letters (Lowercase)": "alpha,beta,gamma,delta,epsilon", // Example for future
    // "Abstract Glyphs (Set A)": "Glyph1,Glyph2,Glyph3,Glyph4", // Names for abstract symbols
};


const SymbolicStimulusPanel = ({ config, onConfigChange, pathPrefix, globalStimulusConfig }) => {
    const currentConfig = config || {};

    const handleChange = (key, value) => {
        onConfigChange(`${pathPrefix}.${key}`, value);
    };

    const handleSetTypeChange = (newSetType) => {
        handleChange('SetType', newSetType);
        // If a predefined set is selected from the SetType dropdown, update SymbolList
        if (PREDEFINED_SYMBOL_SETS[newSetType]) {
            handleChange('SymbolList', PREDEFINED_SYMBOL_SETS[newSetType]);
        } else if (newSetType === "Custom List") {
             // If switching to custom and SymbolList was from predefined, clear or set defaults
            if (!currentConfig.SymbolList || PREDEFINED_SYMBOL_SETS[currentConfig.SetType]) {
                 handleChange('SymbolList', "A,B,C,D"); // Default custom list
            }
        }
    };
    
    // Helper to get the current symbol list as a string for the textarea
    const getSymbolListString = () => {
        if (typeof currentConfig.SymbolList === 'string') {
            return currentConfig.SymbolList;
        } else if (Array.isArray(currentConfig.SymbolList)) {
            return currentConfig.SymbolList.join(',');
        }
        return '';
    };

    return (
        <div className="space-y-4">
            <SubSettingsSection title="Symbol Set Definition" helpText="Define the type and specific list of symbols.">
                <div>
                    <label htmlFor={`${pathPrefix}-SetType`} className="block text-xs font-medium text-gray-600 mb-1">
                        Symbol Set Type
                    </label>
                    <select
                        id={`${pathPrefix}-SetType`}
                        name="SetType"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.SetType || 'Letters'}
                        onChange={(e) => handleSetTypeChange(e.target.value)}
                    >
                        <option value="Letters">Letters</option>
                        <option value="Numbers">Numbers</option>
                        <option value="Playing Card Suits">Playing Card Suits</option>
                        {/* <option value="Abstract Glyphs">Abstract Glyphs (Non-labelable)</option> */}
                        {/* Dynamically add predefined sets to the dropdown */}
                        {Object.keys(PREDEFINED_SYMBOL_SETS).map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                        <option value="Custom List">Custom List (Specify Below)</option>
                    </select>
                </div>

                <div>
                    <label htmlFor={`${pathPrefix}-SymbolList`} className="block text-xs font-medium text-gray-600 mb-1">
                        Symbol List (comma-separated)
                    </label>
                    <textarea
                        id={`${pathPrefix}-SymbolList`}
                        name="SymbolList"
                        rows="3"
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={getSymbolListString()}
                        onChange={(e) => handleChange('SymbolList', e.target.value)} // Store as string directly
                        placeholder="e.g., A,B,C,D,E or Symbol1,Symbol2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Enter symbols separated by commas. For letters, consider phonological distinctness.
                        For abstract glyphs, use unique names that your rendering logic will map to visuals.
                    </p>
                </div>
            </SubSettingsSection>

            {/* Future settings like Font/Appearance for text-based symbols could go here */}
            {/* <SubSettingsSection title="Symbol Appearance (If Applicable)" helpText="Customize font, size, etc. for text-based symbols.">
                <p className="text-sm text-gray-500">Font and appearance settings will be available later.</p>
            </SubSettingsSection> */}

            {/* Debug section (optional) */}
            {/* <details className="mt-4 text-xs">
                <summary className="cursor-pointer text-gray-500">Show Symbolic Panel Debug Info</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded text-gray-700 overflow-auto">
                    Path Prefix: {pathPrefix}{"\n"}
                    Config: {JSON.stringify(currentConfig, null, 2)}
                </pre>
            </details> */}
        </div>
    );
};

SymbolicStimulusPanel.propTypes = {
    config: PropTypes.shape({
        SetType: PropTypes.string,
        SymbolList: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]), // Can be string (CSV) or array
        // Font: PropTypes.string, // Future
        // Appearance: PropTypes.string, // Future
    }),
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Stimulus.Symbolic"
    globalStimulusConfig: PropTypes.object,
};

export default SymbolicStimulusPanel;